import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { API_BASE_URL } from '../lib/utils';
import PRFMainScreen from '../components/prf/PRFMainScreen';
import PRFIncidentSelect from '../components/prf/PRFIncidentSelect';
import PatientDetailsSection from '../components/prf/sections/PatientDetailsSection';
import NOKDetailsSection from '../components/prf/sections/NOKDetailsSection';
import PresentingComplaintSection from '../components/prf/sections/PresentingComplaintSection';
import PastMedicalHistorySection from '../components/prf/sections/PastMedicalHistorySection';
import AllergiesMedsSection from '../components/prf/sections/AllergiesMedsSection';
import FASTSection from '../components/prf/sections/FASTSection';
import ObservationsSection from '../components/prf/sections/ObservationsSection';
import AttendingSection from '../components/prf/sections/AttendingSection';
import type { 
  PatientReportForm, 
  PatientDetails, 
  NOKDetails, 
  PresentingComplaint,
  PastMedicalHistory,
  AllergiesMeds,
  FAST,
  Observations,
  Attending,
  PRFSectionStatus
} from '../types/prf';

type SectionId = 'patientDetails' | 'nokDetails' | 'presentingComplaint' | 'pastMedicalHistory' | 'allergiesMeds' | 'fast' | 'observations' | 'attending';

// Extract the numeric sequence from a reference ID like "EV-12-210426" → "12"
const extractRefNumber = (id: string): string => {
  const match = id.match(/^(?:EV|EVT|INC)-(\d+)/i);
  return match ? match[1] : id;
};

// Generate a PRF reference: PRF-EV-{evNum}-INC-{incNum}-{seq}
// Sequence is per-incident counter persisted in localStorage
const generatePRFRef = (eventNumber: string, incidentNumber: string): string => {
  const evNum = extractRefNumber(eventNumber);
  const incNum = extractRefNumber(incidentNumber);
  const storageKey = `c2_prf_counter_${evNum}_${incNum}`;
  const current = parseInt(localStorage.getItem(storageKey) || '0', 10);
  const next = current + 1;
  localStorage.setItem(storageKey, String(next));
  return `PRF-EV-${evNum}-INC-${incNum}-${next}`;
};

export default function PRFPage() {
  const navigate = useNavigate();
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const [currentSection, setCurrentSection] = useState<SectionId | null>(null);
  const [incidentInfo, setIncidentInfo] = useState<{ eventNumber: string; incidentNumber: string } | null>(null);
  const [prfRef, setPrfRef] = useState<string>('');
  const [showIncidentSelect, setShowIncidentSelect] = useState(false);
  
  // Initialize PRF data
  const [prfData, setPrfData] = useState<Partial<PatientReportForm>>({
    patientDetails: {},
    nokDetails: {},
    presentingComplaint: {},
    pastMedicalHistory: {},
    allergiesMeds: {},
    fast: {},
    observations: {},
    attending: {},
    sectionStatus: {
      patientDetails: 'not_started',
      nokDetails: 'not_started',
      presentingComplaint: 'not_started',
      pastMedicalHistory: 'not_started',
      allergiesMeds: 'not_started',
      fast: 'not_started',
      observations: 'not_started',
      attending: 'not_started',
    },
  });

  // Check if MDT is assigned to an incident
  useEffect(() => {
    if (activeDispatch && activeDispatch.status === 'accepted') {
      // MDT is assigned - auto-populate directly from the active dispatch
      const eventNum = activeDispatch.eventId || 'N/A';
      const incidentNum = activeDispatch.incidentId;
      setIncidentInfo({ eventNumber: eventNum, incidentNumber: incidentNum });
      setPrfRef(generatePRFRef(eventNum, incidentNum));
      setShowIncidentSelect(false);
    } else {
      // MDT is not assigned - show incident selection form
      setShowIncidentSelect(true);
    }
  }, [activeDispatch]);

  const handleSectionSelect = (sectionId: string) => {
    setCurrentSection(sectionId as SectionId);
  };

  const handleBack = () => {
    setCurrentSection(null);
  };

  const handleSaveSection = (sectionId: SectionId, data: any) => {
    setPrfData(prev => ({
      ...prev,
      [sectionId]: data,
      sectionStatus: {
        ...prev.sectionStatus!,
        [sectionId]: 'complete' as PRFSectionStatus,
      },
    }));
    setCurrentSection(null);
  };

  const handleIncidentConfirm = (eventNumber: string, incidentNumber: string) => {
    setIncidentInfo({ eventNumber, incidentNumber });
    setPrfRef(generatePRFRef(eventNumber, incidentNumber));
    setShowIncidentSelect(false);
  };

  const token = useAppSelector((s) => s.auth.token);

  const handleSubmit = async () => {
    if (!incidentInfo || !prfRef) {
      alert('Missing incident or PRF reference');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/prfs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: prfRef,
          eventId: incidentInfo.eventNumber,
          incidentId: incidentInfo.incidentNumber,
          data: prfData,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to submit PRF: ${err.error || res.statusText}`);
        return;
      }
      alert(`PRF ${prfRef} submitted successfully!`);
      navigate(-1);
    } catch (e) {
      alert('Network error submitting PRF');
    }
  };

  // Show incident selection if not assigned and no incident info
  if (showIncidentSelect && !incidentInfo) {
    return (
      <PRFIncidentSelect
        onConfirm={handleIncidentConfirm}
        onBack={() => navigate(-1)}
      />
    );
  }

  // Render current section
  if (currentSection === 'patientDetails') {
    return (
      <PatientDetailsSection
        data={prfData.patientDetails || {}}
        onSave={(data) => handleSaveSection('patientDetails', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'nokDetails') {
    return (
      <NOKDetailsSection
        data={prfData.nokDetails || {}}
        onSave={(data) => handleSaveSection('nokDetails', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'presentingComplaint') {
    return (
      <PresentingComplaintSection
        data={prfData.presentingComplaint || {}}
        onSave={(data) => handleSaveSection('presentingComplaint', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'pastMedicalHistory') {
    return (
      <PastMedicalHistorySection
        data={prfData.pastMedicalHistory || {}}
        onSave={(data) => handleSaveSection('pastMedicalHistory', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'allergiesMeds') {
    return (
      <AllergiesMedsSection
        data={prfData.allergiesMeds || {}}
        onSave={(data) => handleSaveSection('allergiesMeds', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'fast') {
    return (
      <FASTSection
        data={prfData.fast || {}}
        onSave={(data) => handleSaveSection('fast', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'observations') {
    return (
      <ObservationsSection
        data={prfData.observations || {}}
        onSave={(data) => handleSaveSection('observations', data)}
        onBack={handleBack}
      />
    );
  }

  if (currentSection === 'attending') {
    return (
      <AttendingSection
        data={prfData.attending || {}}
        onSave={(data) => handleSaveSection('attending', data)}
        onBack={handleBack}
      />
    );
  }

  // Main PRF screen
  const displayIncidentId = incidentInfo?.incidentNumber || 'N/A';
  
  return (
    <PRFMainScreen
      incidentId={displayIncidentId}
      eventNumber={incidentInfo?.eventNumber}
      prfRef={prfRef}
      onSectionSelect={handleSectionSelect}
      onBack={() => navigate(-1)}
      onSubmit={handleSubmit}
      sectionStatuses={prfData.sectionStatus}
    />
  );
}
